import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe, TitleCasePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import * as L from 'leaflet';
import type { FeatureCollection } from 'geojson';
import { Subscription, forkJoin } from 'rxjs';
import { VigimareAlertService } from '../../services/vigimare-alert.service';
import { CyberSiteService } from '../../services/cyber-site.service';
import { CyberTopologyService } from '../../services/cyber-topology.service';
import { CyberScenarioService } from '../../services/cyber-scenario.service';
import { VigimareAlert } from '../../models/vigimare-alert.model';
import { CyberAlertSeverity, CyberKillChain, CyberSite } from '../../models/cyber-site.model';
import { CyberReplayStatus, CyberScenarioSummary } from '../../models/cyber-scenario.model';
import {
  CyberTopology,
  CyberTopologyDraft,
  CyberTopologySummary,
  emptyDraft,
  toDraft,
} from '../../models/cyber-topology.model';
import { KeycloakService } from '../../security/keycloak.service';
import { APP_ROLES } from '../../security/roles';
import { CyberEvent, alertEvent } from './state/cyber-event.model';
import { CyberFeedService } from './state/cyber-feed.service';
import { CyberXmlService } from './state/cyber-xml';
import { rememberTopologyId, rememberedTopologyId } from './state/remembered-topology';
import {
  alertSeverity,
  initialState,
  isAlarm,
  isAssignedTo,
  namedNodes,
  reduce,
  siteOf,
} from './state/cyber-state';
import { NetworkTopologyComponent } from './components/network-topology';
import { TopologyEditorComponent } from './components/topology-editor';
import { TopologyShareDialogComponent } from './components/topology-share-dialog';
import { XmlSpotlightComponent } from './components/xml-spotlight';

// ─── Local types ────────────────────────────────────────────────────────────

type AlertSeverity = CyberAlertSeverity;

interface LayerDef {
  id: string;
  label: string;
  anomalyTypes: string[];
}

/** One arriving alert, announced over the page until the operator dismisses it. */
interface AlertToast {
  id: number;
  title: string;
  text: string;
  tone: AlertSeverity;
}

/** Alerts collapsed into one marker because they overlap at the current zoom. */
interface AlertCluster {
  lat: number;
  lng: number;
  alerts: VigimareAlert[];
}

/**
 * Marker filters. This is a general alert taxonomy rather than anything site-specific —
 * an infrastructure's own vocabulary lives in its config, not here.
 *
 * Alerts matching none of the named types fall through to the "any alert filter is on"
 * branch in `updateMarkers`, so they remain visible rather than disappearing for want of a
 * filter of their own.
 */
const LAYERS: LayerDef[] = [
  { id: 'cyber', label: 'Cyber Alerts', anomalyTypes: ['cyber'] },
  {
    id: 'cyber-physical',
    label: 'Cyber-Physical',
    anomalyTypes: ['cyber-physical', 'combined cyber-physical'],
  },
  { id: 'infrastructure', label: 'Infrastructure', anomalyTypes: [] },
];

/**
 * How far back the Recent Alerts panel looks.
 *
 * The alert store keeps every replay ever run, so without a window the panel shows last week's
 * demo next to what is happening now and stops being "recent" in any useful sense. `0` hours
 * disables the cutoff.
 */
const RECENT_RANGES: readonly { hours: number; label: string }[] = [
  { hours: 1, label: 'Last hour' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 168, label: 'Last 7 days' },
  { hours: 0, label: 'All' },
];

const DEFAULT_RANGE_HOURS = 24;

const HOUR_MS = 3_600_000;

/** Markers closer than this on screen are collapsed into one clickable cluster. */
const CLUSTER_PX = 28;

/** Least to most urgent — a cluster takes the worst severity among its members. */
const SEVERITY_ORDER: AlertSeverity[] = ['unknown', 'nominal', 'warning', 'critical'];

const TILES = {
  dark: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  light: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
};

// Runs at sea read cool and solid, runs on land warm and dashed, so the two are told apart
// at a glance without needing a key.
const INFRA_SEA = '#0B6E99';
const INFRA_LAND = '#C1440E';

/** Fallback view when no infrastructure is configured — the western Mediterranean. */
const FALLBACK_VIEW: { center: [number, number]; zoom: number } = {
  center: [41.5, 10.5],
  zoom: 6,
};

const EMPTY_KILL_CHAIN: CyberKillChain = { stages: [] };

// ─── Severity helpers ────────────────────────────────────────────────────────
// How an alert is graded lives in `cyber-state`, because it depends on the site config and is
// worth testing on its own. What a grade looks like belongs here.

function severityColor(sev: AlertSeverity): string {
  switch (sev) {
    case 'critical':
      return '#E24B4A';
    case 'warning':
      return '#EF9F27';
    case 'nominal':
      return '#639922';
    default:
      return '#534AB7';
  }
}

function getBadgeClass(alert: VigimareAlert): string {
  const t = (alert.anomaly?.anomalyType ?? '').toLowerCase();
  if (t === 'cyber') return 'badge badge-cyber';
  if (t === 'physical') return 'badge badge-physical';
  if (t.includes('cyber-physical') || t.includes('combined')) return 'badge badge-cp';
  if (alert.intentions?.length > 0) return 'badge badge-cp';
  return 'badge badge-unclassified';
}

function badgeLabel(alert: VigimareAlert): string {
  const t = (alert.anomaly?.anomalyType ?? '').toLowerCase();
  if (t) return t;
  if (alert.intentions?.length > 0) return 'intention';
  return 'unclassified';
}

/**
 * What kind of finding this is, read off what the alert carries rather than off a field.
 *
 * An alert is an anomaly or a set of intentions, never both — the rule the alert service enforces
 * on the way in — so the shape of the alert is the answer. The sub-type comes along because it is
 * the part an operator acts on: "Intention" alone says less than "Intention · cyber".
 */
function alertKindLabel(alert: VigimareAlert): string {
  if (alert.anomaly) return `Anomaly · ${alert.anomaly.anomalyType}`;

  if (alert.intentions?.length > 0) {
    const top = [...alert.intentions].sort((a, b) => b.probability - a.probability)[0];
    return `Intention · ${top.classification}`;
  }

  return 'Unclassified';
}

// ─── Component ───────────────────────────────────────────────────────────────

@Component({
  selector: 'app-cyber',
  imports: [
    CommonModule,
    DatePipe,
    DecimalPipe,
    TitleCasePipe,
    RouterLink,
    NetworkTopologyComponent,
    TopologyEditorComponent,
    TopologyShareDialogComponent,
    XmlSpotlightComponent,
  ],
  templateUrl: './cyber.html',
  styleUrl: './cyber.scss',
  standalone: true,
})
export class CyberComponent implements OnInit, OnDestroy {
  private alertService = inject(VigimareAlertService);
  private siteService = inject(CyberSiteService);
  private topologyService = inject(CyberTopologyService);
  private scenarioService = inject(CyberScenarioService);
  private feedService = inject(CyberFeedService);
  private xmlService = inject(CyberXmlService);
  private keycloak = inject(KeycloakService);
  private zone = inject(NgZone);

  /**
   * Who the remembered topology belongs to. The page is behind a login, so a shared machine must
   * not open on the previous operator's choice; `anonymous` only stands in if the page somehow
   * renders before a token exists, and is stable enough to be a key.
   */
  private readonly userId = this.keycloak.tokenParsed?.sub ?? 'anonymous';

  // ── Helpers exposed to the template ───────────────────────────────────────
  /**
   * Bound rather than imported straight into the template: grading depends on the site config,
   * which is loaded, so it has to be read off the signal at the moment of the call.
   */
  readonly getSeverity = (alert: VigimareAlert): AlertSeverity =>
    alertSeverity(this.sites(), alert);
  readonly getBadgeClass = getBadgeClass;
  readonly badgeLabel = badgeLabel;
  readonly alertKindLabel = alertKindLabel;
  readonly layers = LAYERS;

  // ── Where an alert's full report lives ────────────────────────────────────
  // Incident Management is where an alert is worked: assigned, commented on, audited. Whoever
  // cannot go there is offered the alert on the monitoring page instead, and only for alerts that
  // are actually theirs — a report on someone else's incident is not theirs to open.
  readonly canManageIncidents = this.keycloak.canManageIncidents();

  /**
   * Whether this user sees only their own queue rather than the whole picture.
   *
   * An analyst works the alerts given to them; deciding what to give them is what an operator and
   * an admin are here for, so they keep the full view. The route admits nobody else, so this is
   * exactly "analyst" — spelled as a role plus an exemption rather than as `!canManageIncidents()`
   * so the intent survives the next role being added.
   */
  readonly restrictedToAssigned =
    this.keycloak.hasRole(APP_ROLES.ANALYST) && !this.keycloak.canManageIncidents();

  /**
   * Ids of the alerts assigned to this user, directly or through one of their organizations.
   *
   * Resolved server-side: organization membership is not in the token, and the endpoint that
   * would give it is admin-only. The alert feed carries direct assignments, so this only has to
   * be reloaded to catch an organization assignment made while the page is open.
   *
   * Null until it first arrives — which is not the same as empty. A restricted page shows nothing
   * while it is unknown, rather than showing everything and taking it back a moment later.
   */
  private assignedAlertIds = signal<ReadonlySet<number> | null>(null);

  // ── Configuration ─────────────────────────────────────────────────────────
  // Every configured infrastructure is drawn at once; there is no picker, because the page
  // monitors all of them rather than one at a time.
  sites = signal<CyberSite[]>([]);
  killChain = signal<CyberKillChain>(EMPTY_KILL_CHAIN);

  // ── Topology ──────────────────────────────────────────────────────────────
  topologies = signal<CyberTopologySummary[]>([]);
  topology = signal<CyberTopology | null>(null);
  editorDraft = signal<CyberTopologyDraft | null>(null);
  /** Id the open editor is editing, or null when it is drawing a new topology. */
  editingId = signal<number | null>(null);
  sharingTopology = signal<CyberTopologySummary | null>(null);
  /** Topology waiting on a delete confirmation, or null. */
  pendingDelete = signal<CyberTopology | null>(null);
  topologySaving = signal(false);
  topologyError = signal<string | null>(null);

  ownedTopologies = computed(() => this.topologies().filter((t) => t.owned));
  sharedTopologies = computed(() => this.topologies().filter((t) => !t.owned));
  activeTopologySummary = computed(() => {
    const id = this.topology()?.id;
    return this.topologies().find((t) => t.id === id) ?? null;
  });

  // ── Scenario replay ───────────────────────────────────────────────────────
  // Replaying publishes a recording onto the detection topic; the alerts return over the
  // ordinary feed, so nothing here touches the state the page renders.
  scenarios = signal<CyberScenarioSummary[]>([]);
  selectedScenario = signal<string | null>(null);
  replayStatus = signal<CyberReplayStatus | null>(null);
  replayError = signal<string | null>(null);

  replayRunning = computed(() => this.replayStatus()?.running === true);

  // ── Derived state ─────────────────────────────────────────────────────────
  alerts = signal<VigimareAlert[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);

  // ── Map interaction ───────────────────────────────────────────────────────
  selectedAlert = signal<VigimareAlert | null>(null);

  /**
   * The devices the open alert is about, ringed on the diagram.
   *
   * The diagram otherwise shows the union of everything still wrong, which is the right answer to
   * "what is the state of the plant" and no answer at all to "what is *this* alert about" — and
   * without that, alerts cannot be worked through one at a time, because detectors report
   * overlapping sets of devices.
   *
   * Named rather than flagged nodes, deliberately: an alert that has been resolved no longer
   * flags anything, but what it covered is still worth seeing while reviewing it.
   */
  selectedNodes = computed(() => {
    const alert = this.selectedAlert();
    if (!alert) return new Set<string>();
    return new Set(namedNodes(this.topology(), alert, siteOf(this.sites(), alert)));
  });
  /** The dialog opens centred on the page and only takes pixel coords once dragged. */
  popupCentered = signal(true);
  popupX = signal(20);
  popupY = signal(60);
  /** Alerts sharing a marker, offered for the user to pick from. */
  alertChooser = signal<VigimareAlert[] | null>(null);
  activeLayers = signal<Set<string>>(new Set(LAYERS.map((l) => l.id)));
  basemap = signal<'dark' | 'light'>('dark');

  // ── Alert pop-ups ─────────────────────────────────────────────────────────
  // A stack rather than a single slot: they persist until dismissed, so an alert arriving must
  // not silently replace one the operator has not read yet.
  toasts = signal<AlertToast[]>([]);
  private toastSeq = 0;

  // ── XML spotlight ─────────────────────────────────────────────────────────
  spotlight = signal<{ title: string; subtitle: string | null } | null>(null);
  spotlightXml = signal<string | null>(null);
  spotlightLoading = signal(false);
  spotlightError = signal<string | null>(null);

  // ── What the page shows ───────────────────────────────────────────────────
  /**
   * Whose alerts this page is about, taken from the infrastructure configs rather than named in
   * code: `expectedProducers` already declares which detection services a site expects to hear
   * from, and admitting a new partner should be a config change, not an edit here.
   *
   * Empty means "not configured" rather than "nobody" — while the configs are still loading, or
   * for a site that declares no producers, the page shows everything rather than nothing.
   */
  private acceptedProducers = computed(
    () => new Set(this.sites().flatMap((site) => site.expectedProducers ?? [])),
  );

  // Everything the user can click — map markers, the alert list, the pills — works off this one
  // set, so scoping is applied here and nowhere else.
  //
  // Only scoping. Whether an alert is a detector repeating itself was decided before it was
  // stored, by the listener that ingested it; deciding it a second time here, on a different
  // reading of what makes two alerts the same, only hid rows the store had deliberately kept and
  // left this panel disagreeing with the alert table, the notifications and its own pop-ups.
  survivingAlerts = computed(() =>
    this.alerts().filter((alert) => this.isAccepted(alert) && this.isVisibleToMe(alert)),
  );

  private isAccepted(alert: VigimareAlert): boolean {
    const accepted = this.acceptedProducers();
    return accepted.size === 0 || accepted.has(alert.legalName);
  }

  /**
   * Whether this user is shown this alert at all.
   *
   * Applied here, with the producer filter and ahead of duplicate suppression, so it reaches
   * everything at once — the pills, the markers, the list, the diagram — and so an alert the user
   * cannot see never anchors a suppression window in front of one they can.
   */
  private isVisibleToMe(alert: VigimareAlert): boolean {
    if (!this.restrictedToAssigned) return true;

    const assigned = this.assignedAlertIds();
    return assigned !== null && isAssignedTo(alert, this.userId, assigned);
  }

  // ── Computed views ────────────────────────────────────────────────────────
  criticalCount = computed(
    () => this.survivingAlerts().filter((a) => this.getSeverity(a) === 'critical').length,
  );
  warningCount = computed(
    () => this.survivingAlerts().filter((a) => this.getSeverity(a) === 'warning').length,
  );
  nominalCount = computed(
    () =>
      this.survivingAlerts().filter(
        (a) => this.getSeverity(a) === 'nominal' || this.getSeverity(a) === 'unknown',
      ).length,
  );

  // ── Recent Alerts panel ───────────────────────────────────────────────────
  // The pills and the map markers deliberately stay outside this window: they summarise everything
  // the page knows about, and narrowing them with the list would make them say something else. The
  // network diagram does follow it — see `cyber` below.
  readonly recentRanges = RECENT_RANGES;
  recentRangeHours = signal(DEFAULT_RANGE_HOURS);

  recentAlerts = computed(() => {
    const hours = this.recentRangeHours();
    const cutoff = hours > 0 ? this.now() - hours * HOUR_MS : null;

    return this.survivingAlerts()
      .filter((alert) => {
        if (cutoff === null) return true;
        const at = Date.parse(alert.generatedIn);
        // An unreadable timestamp cannot be placed in the window; keep it rather than hide it.
        return !Number.isFinite(at) || at >= cutoff;
      })
      .sort((a, b) => b.generatedIn.localeCompare(a.generatedIn));
  });

  onRecentRangeChange(event: Event): void {
    this.recentRangeHours.set(Number((event.target as HTMLSelectElement).value));
  }

  /**
   * The story the page tells — flagged nodes, compromised links, node traffic, the kill chain —
   * derived from the alerts currently listed rather than accumulated as they arrive.
   *
   * A diagram is a claim about now. Folded over the arriving feed, the claim only ever grew: a
   * node flagged by an alert from last week stayed red, and the link animation with it, long after
   * anything was happening. Recomputing from Recent Alerts makes the range selector the answer to
   * "since when" and lets the diagram go quiet on its own as alerts age out of the window.
   *
   * `reduce` is a pure fold, so replaying it is exactly equivalent to having accumulated it, and
   * the list is already producer-filtered and deduplicated.
   */
  cyber = computed(() => {
    const context = {
      sites: this.sites(),
      killChain: this.killChain(),
      topology: this.topology(),
    };

    return [...this.recentAlerts()]
      .sort((a, b) => a.generatedIn.localeCompare(b.generatedIn))
      .reduce((state, alert) => reduce(state, alertEvent(alert), context), initialState());
  });

  // ── Drag state ────────────────────────────────────────────────────────────
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartPopupX = 0;
  private dragStartPopupY = 0;

  // ── Internal Leaflet state ────────────────────────────────────────────────
  private mapEl = viewChild<ElementRef<HTMLDivElement>>('mapEl');
  /** Read once, to convert the centred dialog to pixel coordinates on drag. */
  private popupEl = viewChild<ElementRef<HTMLDivElement>>('popupEl');
  private map?: L.Map;
  private tileLayer?: L.TileLayer;
  private markerLayer = L.layerGroup();
  private infraLayer = L.layerGroup();

  // ── Subscriptions ─────────────────────────────────────────────────────────
  private feedSubscription?: Subscription;
  private configSubscription?: Subscription;
  /** Poll handle, live only while a replay is running. */
  private replayPoll?: ReturnType<typeof setInterval>;

  /**
   * The Recent Alerts cutoff is relative to now, but a computed only re-runs when a signal it
   * reads changes — on a quiet page an alert would sit in the list long after it aged out, which
   * shows up immediately on the "Last hour" setting. A minute is finer than any range needs.
   */
  private now = signal(Date.now());
  private clock?: ReturnType<typeof setInterval>;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  constructor() {
    // The map container only exists once the config has loaded, so the map is built when
    // the element appears rather than in a lifecycle hook that runs before it does.
    effect(() => {
      const host = this.mapEl();
      if (!host || this.map) return;
      this.zone.runOutsideAngular(() => this.initMap(host.nativeElement));
    });

    // The cable is drawn in alarm colours once the story reaches its last stage, and the story is
    // now derived — so the drawing has to follow it rather than only the events that used to
    // rebuild it (config load, map init, a layer toggle).
    effect(() => {
      this.cyber();
      if (!this.map) return;
      this.zone.runOutsideAngular(() => this.drawInfrastructure());
    });
  }

  ngOnInit(): void {
    this.loadConfiguration();
    this.loadTopologies();
    this.loadScenarios();
    this.loadAssignedAlerts();
    this.clock = setInterval(() => this.now.set(Date.now()), 60_000);
  }

  ngOnDestroy(): void {
    this.feedSubscription?.unsubscribe();
    this.configSubscription?.unsubscribe();
    this.stopPollingReplay();
    clearInterval(this.clock);
    this.map?.remove();
    this.map = undefined;
  }

  // ── Configuration ─────────────────────────────────────────────────────────

  private loadConfiguration(): void {
    this.configSubscription = forkJoin({
      sites: this.siteService.listAllSites(),
      killChain: this.siteService.getKillChain(),
    }).subscribe({
      next: ({ sites, killChain }) => {
        this.sites.set(sites);
        this.killChain.set(killChain);
        this.loading.set(false);

        if (sites.length === 0) {
          this.error.set('No monitored infrastructure is configured on the server.');
        }

        this.zone.runOutsideAngular(() => {
          this.drawInfrastructure();
          this.fitToInfrastructure();
        });
        this.startFeed();
      },
      error: (err: { message?: string }) => {
        this.error.set('Failed to load the cyber configuration. ' + (err?.message ?? ''));
        this.loading.set(false);
      },
    });
  }

  // ── Topology ──────────────────────────────────────────────────────────────

  private loadTopologies(preferId?: number): void {
    this.topologyService.list().subscribe({
      next: (summaries) => {
        this.topologies.set(summaries);

        // Last link in the chain: what this user was looking at on their previous visit. A
        // remembered topology that has since been deleted, or that stopped being shared with
        // them, is simply not in the list and falls through to the first one.
        const wanted = preferId ?? this.topology()?.id ?? rememberedTopologyId(this.userId);
        const next = summaries.find((t) => t.id === wanted) ?? summaries[0];
        if (next) {
          this.selectTopology(next.id);
        } else {
          this.topology.set(null);
        }
      },
      error: (err: { message?: string }) => {
        this.topologyError.set('Failed to load topologies. ' + (err?.message ?? ''));
      },
    });
  }

  selectTopology(id: number): void {
    // Recorded before the guard: re-picking what is already shown is still the user saying this
    // is the one they want back next time.
    rememberTopologyId(this.userId, id);

    if (this.topology()?.id === id) return;
    this.topologyError.set(null);
    this.topologyService.get(id).subscribe({
      next: (topology) => this.topology.set(topology),
      error: (err: { message?: string }) =>
        this.topologyError.set('Failed to load the topology. ' + (err?.message ?? '')),
    });
  }

  onTopologyChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    if (value) this.selectTopology(Number(value));
  }

  newTopology(): void {
    // A new diagram is anchored to the first configured infrastructure, which is the only
    // one there is today; the field is editable server-side when that changes.
    this.editingId.set(null);
    this.editorDraft.set(emptyDraft(this.sites()[0]?.id ?? null));
  }

  editTopology(): void {
    const topology = this.topology();
    if (!topology?.owned) return;
    this.editingId.set(topology.id);
    this.editorDraft.set(toDraft(topology));
  }

  cancelEditing(): void {
    this.editorDraft.set(null);
    this.editingId.set(null);
    this.topologyError.set(null);
  }

  saveTopology(draft: CyberTopologyDraft): void {
    const id = this.editingId();

    this.topologySaving.set(true);
    this.topologyError.set(null);

    const request$ =
      id === null ? this.topologyService.create(draft) : this.topologyService.update(id, draft);

    request$.subscribe({
      next: (saved) => {
        this.topology.set(saved);
        this.topologySaving.set(false);
        this.editorDraft.set(null);
        this.editingId.set(null);
        this.loadTopologies(saved.id);
      },
      error: (err: { error?: { message?: string }; message?: string }) => {
        this.topologyError.set(
          err?.error?.message ?? err?.message ?? 'Could not save the topology.',
        );
        this.topologySaving.set(false);
      },
    });
  }

  openShare(): void {
    const summary = this.activeTopologySummary();
    if (summary?.owned) this.sharingTopology.set(summary);
  }

  closeShare(): void {
    this.sharingTopology.set(null);
  }

  deleteTopology(): void {
    const topology = this.topology();
    if (!topology?.owned) return;
    this.pendingDelete.set(topology);
  }

  cancelDelete(): void {
    this.pendingDelete.set(null);
  }

  confirmDelete(): void {
    const topology = this.pendingDelete();
    if (!topology) return;
    this.pendingDelete.set(null);

    this.topologyService.delete(topology.id).subscribe({
      next: () => {
        this.topology.set(null);
        this.loadTopologies();
      },
      error: (err: { message?: string }) =>
        this.topologyError.set('Could not delete the topology. ' + (err?.message ?? '')),
    });
  }

  // ── Scenario replay ───────────────────────────────────────────────────────

  private loadScenarios(): void {
    this.scenarioService.list().subscribe({
      next: (scenarios) => {
        this.scenarios.set(scenarios);
        this.selectedScenario.set(scenarios[0]?.id ?? null);
        // A replay started elsewhere — another tab, an earlier visit — is still running on the
        // server, so adopt it rather than offering to start a second one.
        this.refreshReplay();
      },
      error: () => this.scenarios.set([]),
    });
  }

  onScenarioChange(event: Event): void {
    this.selectedScenario.set((event.target as HTMLSelectElement).value || null);
  }

  toggleReplay(): void {
    if (this.replayRunning()) {
      this.stopReplay();
    } else {
      this.startReplay();
    }
  }

  private startReplay(): void {
    const id = this.selectedScenario();
    if (!id) return;

    this.replayError.set(null);
    this.scenarioService.start(id).subscribe({
      next: (status) => this.applyReplayStatus(status),
      error: (err: { error?: { message?: string }; message?: string }) =>
        this.replayError.set(
          'Could not start the replay. ' + (err?.error?.message ?? err?.message ?? ''),
        ),
    });
  }

  private stopReplay(): void {
    this.scenarioService.stop().subscribe({
      next: (status) => this.applyReplayStatus(status),
      error: (err: { message?: string }) =>
        this.replayError.set('Could not stop the replay. ' + (err?.message ?? '')),
    });
  }

  private refreshReplay(): void {
    this.scenarioService.status().subscribe({
      next: (status) => this.applyReplayStatus(status),
      error: () => this.stopPollingReplay(),
    });
  }

  /**
   * The run is server-side, so progress is polled. Polling only exists while something is
   * running: a finished run reports its closing state once and the timer goes with it.
   */
  private applyReplayStatus(status: CyberReplayStatus): void {
    this.replayStatus.set(status);
    if (status.error) {
      this.replayError.set('The replay stopped: ' + status.error);
    }

    if (status.running && !this.replayPoll) {
      this.replayPoll = setInterval(() => this.refreshReplay(), 2000);
    } else if (!status.running) {
      this.stopPollingReplay();
    }
  }

  private stopPollingReplay(): void {
    if (this.replayPoll) {
      clearInterval(this.replayPoll);
      this.replayPoll = undefined;
    }
  }

  // ── Feed wiring ───────────────────────────────────────────────────────────

  private startFeed(): void {
    this.feedSubscription?.unsubscribe();
    this.feedSubscription = this.feedService.live().subscribe({
      next: (event) => this.ingest(event),
      error: (err: { message?: string }) =>
        this.error.set('Live feed error. ' + (err?.message ?? '')),
    });
  }

  /** Records one alert and refreshes the map; everything derived from it follows the signal. */
  private ingest(event: CyberEvent): void {
    this.upsertAlert(event.alert);

    // An assignment — made or withdrawn — reaches this page as an update addressed to the user it
    // concerns, so this is the moment to ask the server again who owns what. Only updates: during a
    // replay every alert is new, and a brand-new alert cannot be assigned to anyone yet.
    if (event.kind === 'updated' && this.restrictedToAssigned) {
      this.loadAssignedAlerts();
    }

    this.announce(event);
  }

  private upsertAlert(alert: VigimareAlert): void {
    this.alerts.update((prev) => {
      const index = prev.findIndex((a) => a.id === alert.id);
      return index >= 0 ? prev.map((a) => (a.id === alert.id ? alert : a)) : [alert, ...prev];
    });
    this.zone.runOutsideAngular(() => this.updateMarkers());
  }

  /**
   * Raises a pop-up for an alert that has just arrived.
   *
   * Only for alerts that have just been created: the feed replays stored history on load, and
   * announcing that would bury the page under everything that ever happened. Only for alerts the
   * page is showing, so the pop-ups agree with what is listed and drawn rather than reporting
   * something invisible.
   */
  private announce(event: CyberEvent): void {
    const alert = event.alert;
    if (event.kind !== 'created' || !this.isAccepted(alert) || !this.isVisibleToMe(alert)) return;

    const tone = this.getSeverity(alert);
    this.toasts.update((prev) => [
      ...prev,
      {
        id: ++this.toastSeq,
        title: `${tone.charAt(0).toUpperCase()}${tone.slice(1)} Alert`,
        text: alert.anomaly?.explanation ?? alert.intentions[0]?.explanation ?? '',
        tone,
      },
    ]);
  }

  dismissToast(id: number): void {
    this.toasts.update((prev) => prev.filter((t) => t.id !== id));
  }

  // ── XML spotlight ─────────────────────────────────────────────────────────

  openAlertXml(alert: VigimareAlert): void {
    this.spotlight.set({ title: 'VigimareAlert', subtitle: alert.uuid });
    this.spotlightXml.set(null);
    this.spotlightError.set(null);
    this.spotlightLoading.set(true);

    this.xmlService.alertXml(alert).subscribe({
      next: (xml) => {
        this.spotlightXml.set(xml);
        this.spotlightLoading.set(false);
      },
      error: (err: { message?: string }) => {
        this.spotlightError.set('Could not load the message XML. ' + (err?.message ?? ''));
        this.spotlightLoading.set(false);
      },
    });
  }

  closeSpotlight(): void {
    this.spotlight.set(null);
    this.spotlightXml.set(null);
    this.spotlightError.set(null);
  }

  // ── Map ───────────────────────────────────────────────────────────────────

  private initMap(container: HTMLElement): void {
    const map = L.map(container, {
      center: FALLBACK_VIEW.center,
      zoom: FALLBACK_VIEW.zoom,
      zoomControl: true,
      // The corner is left bare: the tile credit and each site's route caveat used to live here,
      // and the control is off rather than emptied so it cannot draw its own Leaflet credit either.
      attributionControl: false,
    });
    this.map = map;

    this.tileLayer = L.tileLayer(TILES[this.basemap()], {
      maxZoom: 18,
    }).addTo(map);

    this.infraLayer.addTo(map);
    this.markerLayer.addTo(map);

    // Clustering is screen-space, so it has to be rebuilt whenever the scale changes.
    map.on('zoomend', () => this.updateMarkers());

    // The config loaded before the container existed, so draw what is already known.
    this.drawInfrastructure();
    this.fitToInfrastructure();
    this.updateMarkers();
  }

  toggleBasemap(): void {
    const next = this.basemap() === 'dark' ? 'light' : 'dark';
    this.basemap.set(next);
    this.tileLayer?.setUrl(TILES[next]);
  }

  /**
   * Frames every configured infrastructure at once. With one site this reproduces its own
   * centre and zoom; with several it widens to hold them all, which is the point of having
   * no site picker.
   */
  private fitToInfrastructure(): void {
    const map = this.map;
    const sites = this.sites();
    if (!map || sites.length === 0) return;

    if (sites.length === 1) {
      map.setView(sites[0].map.center, sites[0].map.zoom);
      return;
    }

    const points = sites.flatMap((site) => [
      site.map.center,
      ...(site.map.markers ?? []).map((marker) => marker.pos),
      ...(site.map.lines ?? []).flatMap((line) => line.path),
    ]);
    const bounds = L.latLngBounds(points);
    // Route geometry is the authority on extent where a site has it.
    for (const site of sites) {
      if (site.map.geojson) bounds.extend(L.geoJSON(site.map.geojson).getBounds());
    }
    map.fitBounds(bounds, { padding: [30, 30] });
  }

  /** Draws every infrastructure — cable runs, landing points, substations. */
  private drawInfrastructure(): void {
    this.infraLayer.clearLayers();
    if (!this.activeLayers().has('infrastructure')) return;

    const alarm = isAlarm(this.killChain(), this.cyber());

    for (const site of this.sites()) {
      if (site.map.geojson) {
        this.drawGeoJson(site.map.geojson, alarm);
      }

      for (const line of site.map.lines ?? []) {
        L.polyline(line.path, {
          color: alarm ? '#e24b4a' : '#534ab7',
          weight: 3,
          dashArray: '8, 5',
          opacity: 0.9,
        })
          .bindTooltip(line.name, { sticky: true })
          .addTo(this.infraLayer);
      }

      for (const marker of site.map.markers ?? []) {
        const landing = marker.kind === 'landing';
        L.circleMarker(marker.pos, {
          radius: landing ? 6 : 4,
          color: landing ? '#38bdf8' : '#64748b',
          fillColor: landing ? '#38bdf8' : '#64748b',
          fillOpacity: 0.9,
          weight: 1,
        })
          .bindTooltip(marker.name, { sticky: true })
          .addTo(this.infraLayer);
      }
    }
  }

  /**
   * Draws a route supplied as GeoJSON.
   *
   * Everything is driven by each feature's `category`, so the vocabulary lives in the data:
   * runs at sea read solid and cool, runs on land dashed and warm, and the three kinds of
   * fixed asset get their own shape. Hovering names a feature, which is all the route has to
   * say — it is context for the alerts drawn over it, not the subject of the page.
   */
  private drawGeoJson(collection: FeatureCollection, alarm: boolean): void {
    L.geoJSON(collection, {
      style: (feature) => {
        const submarine = feature?.properties?.['category'] === 'submarine_cable';
        return {
          color: alarm ? '#e24b4a' : submarine ? INFRA_SEA : INFRA_LAND,
          weight: submarine ? 4 : 2.5,
          dashArray: submarine ? undefined : '7, 5',
          opacity: 0.95,
        };
      },
      pointToLayer: (feature, latlng) => {
        const category = feature.properties?.['category'];
        return L.marker(latlng, {
          icon: L.divIcon({
            className: '',
            html: `<div class="infra-node infra-${category ?? 'other'}"></div>`,
            iconSize: category === 'converter_station' ? [14, 14] : [10, 10],
            iconAnchor: category === 'converter_station' ? [7, 7] : [5, 5],
          }),
          // Below the alert markers: the route is context, the alerts are the point.
          zIndexOffset: -500,
        });
      },
      onEachFeature: (feature, layer) => {
        const name = feature.properties?.['name'];
        if (!name) return;

        layer.bindTooltip(name, { sticky: true });
      },
    }).addTo(this.infraLayer);
  }

  /** Alerts that survived deduplication, pass the filters, and have somewhere to go. */
  private visibleAlerts(): VigimareAlert[] {
    const active = this.activeLayers();
    const alertLayers = LAYERS.filter((l) => l.id !== 'infrastructure');

    return this.survivingAlerts().filter((alert) => {
      if ((alert.resolvedLatitude ?? alert.latitude) === null) return false;
      if ((alert.resolvedLongitude ?? alert.longitude) === null) return false;

      const type = (alert.anomaly?.anomalyType ?? '').toLowerCase();
      const specificLayer = alertLayers.find((l) => l.anomalyTypes.some((t) => type.includes(t)));
      // An alert with no filter of its own rides along with any alert filter being on.
      return specificLayer ? active.has(specificLayer.id) : alertLayers.some((l) => active.has(l.id));
    });
  }

  /**
   * Collapses alerts whose markers would sit on top of each other into one cluster. Greedy
   * and screen-space, so it re-forms differently at each zoom level — zooming in separates
   * alerts that only looked coincident when zoomed out.
   */
  private clusterAlerts(alerts: VigimareAlert[]): AlertCluster[] {
    const map = this.map;
    const clusters: AlertCluster[] = [];
    // Anchor screen positions, kept alongside `clusters` so each alert is projected once
    // rather than on every comparison.
    const anchors: L.Point[] = [];

    for (const alert of alerts) {
      const lat = (alert.resolvedLatitude ?? alert.latitude) as number;
      const lng = (alert.resolvedLongitude ?? alert.longitude) as number;
      const point = map?.latLngToContainerPoint([lat, lng]);

      const index = point
        ? anchors.findIndex((anchor) => anchor.distanceTo(point) <= CLUSTER_PX)
        : clusters.findIndex((c) => c.lat === lat && c.lng === lng);

      if (index >= 0) {
        clusters[index].alerts.push(alert);
      } else {
        clusters.push({ lat, lng, alerts: [alert] });
        if (point) anchors.push(point);
      }
    }
    return clusters;
  }

  updateMarkers(): void {
    this.markerLayer.clearLayers();

    for (const cluster of this.clusterAlerts(this.visibleAlerts())) {
      // A cluster is as urgent as its most urgent member.
      const sev = SEVERITY_ORDER.reduce<AlertSeverity>(
        (worst, candidate) =>
          cluster.alerts.some((a) => this.getSeverity(a) === candidate) ? candidate : worst,
        'unknown',
      );
      const count = cluster.alerts.length;
      const color = severityColor(sev);
      const glyph =
        count > 1
          ? String(count)
          : sev === 'critical'
            ? '!'
            : sev === 'warning'
              ? '▲'
              : sev === 'nominal'
                ? '✓'
                : '?';
      const size = count > 1 ? 30 : 26;

      const divIcon = L.divIcon({
        html: `<div class="cyber-marker marker-${sev}${count > 1 ? ' cyber-marker-cluster' : ''}" style="background:${color}">${glyph}</div>`,
        className: '',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });

      const marker = L.marker([cluster.lat, cluster.lng], { icon: divIcon });
      marker.on('click', (e: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(e);
        this.zone.run(() => {
          if (count === 1) {
            this.openPopup(cluster.alerts[0]);
          } else {
            this.alertChooser.set(cluster.alerts);
          }
        });
      });
      marker.addTo(this.markerLayer);
    }

    // The cable recolours with the alarm state, so redraw it alongside the markers.
    this.drawInfrastructure();
  }

  // ── Popup ─────────────────────────────────────────────────────────────────

  /** Opens the dialog centred on the page; it only takes coordinates once dragged. */
  openPopup(alert: VigimareAlert): void {
    this.selectedAlert.set(alert);
    this.popupCentered.set(true);
    this.alertChooser.set(null);
    // Whether the report button appears is decided here, so this is the moment the answer has to
    // be current — an alert may have been assigned since the page was opened.
    this.loadAssignedAlerts();
  }

  /**
   * Whether this alert is this user's to work on.
   *
   * Two sources because they answer at different times: the list resolves organization membership,
   * which only the server knows, while the alert itself carries its direct assignments and so
   * reflects one made moments ago over the live feed.
   */
  isAssignedToMe(alert: VigimareAlert): boolean {
    return isAssignedTo(alert, this.userId, this.assignedAlertIds());
  }

  private loadAssignedAlerts(): void {
    this.alertService.getAssigned('all').subscribe({
      next: (alerts) => this.assignedAlertIds.set(new Set(alerts.map((alert) => alert.id))),
      // Nothing to tell the operator: the only consequence is that a report button stays hidden,
      // and the alert is still reachable from the monitoring page itself.
      error: () => undefined,
    });
  }

  closePopup(): void {
    this.selectedAlert.set(null);
  }

  closeChooser(): void {
    this.alertChooser.set(null);
  }

  openPopupFromList(alert: VigimareAlert): void {
    const lat = alert.resolvedLatitude ?? alert.latitude;
    const lng = alert.resolvedLongitude ?? alert.longitude;
    const map = this.map;
    if (map && lat !== null && lng !== null) {
      this.zone.runOutsideAngular(() => map.flyTo([lat, lng], 8, { duration: 0.8 }));
    }
    this.openPopup(alert);
  }

  // ── Popup drag ────────────────────────────────────────────────────────────

  onPopupHeaderMouseDown(event: MouseEvent): void {
    // A centred dialog has no pixel position yet — take its rendered one and hand over to
    // absolute positioning before the first drag delta is applied.
    if (this.popupCentered()) {
      const rect = this.popupEl()?.nativeElement.getBoundingClientRect();
      this.popupX.set(rect?.left ?? 20);
      this.popupY.set(rect?.top ?? 60);
      this.popupCentered.set(false);
    }

    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartPopupX = this.popupX();
    this.dragStartPopupY = this.popupY();
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.popupX.set(this.dragStartPopupX + (event.clientX - this.dragStartX));
    this.popupY.set(this.dragStartPopupY + (event.clientY - this.dragStartY));
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    this.isDragging = false;
  }

  // ── Layer toggles ─────────────────────────────────────────────────────────

  isLayerActive(id: string): boolean {
    return this.activeLayers().has(id);
  }

  toggleLayer(id: string): void {
    const current = new Set(this.activeLayers());
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    this.activeLayers.set(current);
    this.zone.runOutsideAngular(() => this.updateMarkers());
  }

  // ── Popup helpers ─────────────────────────────────────────────────────────

  isLit(alertSev: AlertSeverity, light: 'red' | 'amber' | 'green'): boolean {
    if (alertSev === 'critical' && light === 'red') return true;
    if (alertSev === 'warning' && light === 'amber') return true;
    if ((alertSev === 'nominal' || alertSev === 'unknown') && light === 'green') return true;
    return false;
  }

  confidencePercent(alert: VigimareAlert): number {
    const score = alert.anomaly?.anomalyScore ?? null;
    if (score !== null) return Math.round(score * 100);
    if (alert.intentions?.length > 0) {
      return Math.round(Math.max(...alert.intentions.map((i) => i.probability)) * 100);
    }
    return 0;
  }
}
